export interface AssetProjection {
	value: number
	dividendYield: number // e.g. 4 for 4%
	dividendGrowth: number // e.g. 5 for 5%
	drip?: boolean
	taxWithholdingRate?: number
	buyFee?: number // percentage
	buyFeeFlat?: number // flat amount per DRIP event
}

export interface ContributionStep {
	fromYear: number // relative (0 = now)
	toYear: number // inclusive
	monthlyAmount: number
}

export interface ProjectionInput {
	assets: AssetProjection[]
	contribution: number
	frequency: "Monthly" | "Quarterly" | "Annually"
	drip: boolean
	priceAppreciation?: number
	years?: number
	dividendGrowthCap?: number
	incomeGoal?: number
	contributionSchedule?: ContributionStep[]
	/** After this many years, flatten all per-position growth rates to their
	 *  income-weighted average at that point. Prevents unsustainable high
	 *  historical rates from compounding unrealistically over long horizons. */
	flattenAfterYear?: number
}

export interface YearData {
	year: number
	portfolioValue: number
	annualIncome: number
	totalContributed: number
	goalProgress: number
}

export interface ProjectionResult {
	data: YearData[]
	freedomYear: number | null
	/** The income-weighted growth rate all positions settled to after flattenAfterYear.
	 *  Only set when flattenAfterYear is used. */
	flattenedGrowthRate?: number
}

export function calculateProjection({
	assets,
	contribution,
	frequency,
	drip,
	priceAppreciation = 6.0,
	years = 50,
	dividendGrowthCap,
	incomeGoal,
	contributionSchedule,
	flattenAfterYear,
}: ProjectionInput): ProjectionResult {
	const data: YearData[] = []
	const annualGoal = incomeGoal ? incomeGoal * 12 : 0
	let freedomYear: number | null = null
	let flattenedGrowthRate: number | undefined

	let currentAssets = assets.map((a) => ({
		value: a.value,
		income: a.value * (a.dividendYield / 100),
		growth: dividendGrowthCap !== undefined ? Math.min(a.dividendGrowth, dividendGrowthCap) : a.dividendGrowth,
		yield: a.dividendYield,
	}))

	let totalContributed = 0
	let baseAnnualContribution = 0

	switch (frequency) {
		case "Monthly":
			baseAnnualContribution = contribution * 12
			break
		case "Quarterly":
			baseAnnualContribution = contribution * 4
			break
		case "Annually":
			baseAnnualContribution = contribution
			break
	}

	const currentYear = new Date().getFullYear()

	let totalValue = currentAssets.reduce((sum, a) => sum + a.value, 0)
	let totalIncome = currentAssets.reduce((sum, a) => sum + a.income, 0)

	const goalProgress0 = annualGoal > 0 ? Math.min(totalIncome / annualGoal, 1) : 0

	data.push({
		year: currentYear,
		portfolioValue: Math.round(totalValue),
		annualIncome: Math.round(totalIncome),
		totalContributed: 0,
		goalProgress: goalProgress0,
	})

	if (annualGoal > 0 && totalIncome >= annualGoal) {
		freedomYear = currentYear
	}

	const hasPerAssetDrip = assets.some((a) => a.drip !== undefined)

	for (let i = 1; i <= years; i++) {
		// Resolve contribution for this year
		let annualContribution = baseAnnualContribution
		if (contributionSchedule && contributionSchedule.length > 0) {
			const step = contributionSchedule.find((s) => i >= s.fromYear && i <= s.toYear)
			annualContribution = step ? step.monthlyAmount * 12 : 0
		}

		let dripIncome = 0
		if (drip) {
			if (hasPerAssetDrip) {
				// Per-asset DRIP: only reinvest positions with drip enabled,
				// accounting for tax withholding and fees
				currentAssets.forEach((a, idx) => {
					if (assets[idx]?.drip) {
						const rate = assets[idx].taxWithholdingRate ?? 0
						const postTax = a.income * (1 - rate / 100)
						const pctFee = (assets[idx].buyFee ?? 0) / 100
						const flatFee = assets[idx].buyFeeFlat ?? 0
						// Approximate: 4 DRIP events/year (most common = quarterly)
						const annualFlatFee = flatFee * 4
						const totalFee = postTax * pctFee + annualFlatFee
						dripIncome += Math.max(0, postTax - totalFee)
					}
				})
			} else {
				// No per-asset flags: reinvest all dividend income
				dripIncome = totalIncome
			}
		}
		const reinvestment = annualContribution + dripIncome
		totalContributed += annualContribution

		currentAssets = currentAssets.map((asset) => {
			const nextValueNoInv = asset.value * (1 + priceAppreciation / 100)
			let nextIncomeNoInv = asset.income * (1 + asset.growth / 100)

			if (nextIncomeNoInv > nextValueNoInv && nextValueNoInv > 0) {
				nextIncomeNoInv = nextValueNoInv
			}

			const share = totalValue > 0 ? asset.value / totalValue : 1 / currentAssets.length
			const assetReinvestment = reinvestment * share
			const currentMarketYield = asset.yield / 100

			return {
				...asset,
				value: nextValueNoInv + assetReinvestment,
				income: nextIncomeNoInv + assetReinvestment * currentMarketYield,
			}
		})

		totalValue = currentAssets.reduce((sum, a) => sum + a.value, 0)
		totalIncome = currentAssets.reduce((sum, a) => sum + a.income, 0)

		// After the transition year, flatten all growth rates to the
		// income-weighted average so high historical rates don't compound
		// unrealistically over long horizons.
		if (flattenAfterYear !== undefined && i === flattenAfterYear && totalIncome > 0) {
			const weightedGrowth = currentAssets.reduce(
				(sum, a) => sum + a.growth * (a.income / totalIncome),
				0,
			)
			currentAssets = currentAssets.map((a) => ({ ...a, growth: weightedGrowth }))
			flattenedGrowthRate = weightedGrowth
		}

		const goalProgress = annualGoal > 0 ? Math.min(totalIncome / annualGoal, 1) : 0
		const calendarYear = currentYear + i

		data.push({
			year: calendarYear,
			portfolioValue: Math.round(totalValue),
			annualIncome: Math.round(totalIncome),
			totalContributed: Math.round(totalContributed),
			goalProgress,
		})

		if (freedomYear === null && annualGoal > 0 && totalIncome >= annualGoal) {
			freedomYear = calendarYear
		}
	}

	return { data, freedomYear, flattenedGrowthRate }
}
